import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useM501, ItemState } from './hooks/useM501';
import { m501Api } from '../../services/m501Api';
import { BatchAcceptanceBridge, pwm501f1AcceptanceManifest } from '../../features/acceptance';
import './M501Form.css';

// -- Helpers --
const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  return v.substring(0, 10);
};

// -- Component --
const M501Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('ZZZZZZZZ');

  const {
    items,
    loading,
    approveLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleUpdateActionTaken,
    handleApprove,
  } = useM501();

  // -- Query --
  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // Auto-fill end range when start field is filled (mirrors FoxPro Text1.Valid)
  const onStartBlur = useCallback(() => {
    if (startNo.trim() && !endNo.trim()) {
      setEndNo(startNo.trim() + 'zzzz');
    }
  }, [startNo, endNo]);

  // -- Approve --
  const onApprove = useCallback(() => {
    Modal.confirm({
      title: '核准確認!',
      content: `確定要核准已勾選的 ${selectedCount} 筆調整單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleApprove();
      },
    });
  }, [selectedCount, handleApprove]);

  // -- Select All --
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;
  const candidateRecords = items.map((state) => state.item);
  const selectedKeys = items.filter((state) => state.selected).map((state) => state.item.fm110_01);
  const queryAcceptanceExact = useCallback(
    (id: string) => m501Api.listCandidates({ startNo: id, endNo: id }),
    [],
  );

  // -- Table columns --
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fm110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '調整單',
      dataIndex: ['item', 'fm110_01'],
      key: 'fm110_01',
      width: 90,
    },
    {
      title: '異動日期',
      dataIndex: ['item', 'fm110_11'],
      key: 'fm110_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '庫位',
      dataIndex: ['item', 'fm110_13'],
      key: 'fm110_13',
      width: 120,
      ellipsis: true,
    },
    {
      title: '輸單人員',
      dataIndex: ['item', 'fm110_15'],
      key: 'fm110_15',
      width: 100,
      ellipsis: true,
    },
    {
      title: '處理對策或說明',
      dataIndex: ['item', 'fm110_22'],
      key: 'fm110_22',
      ellipsis: true,
      render: (v: string, record: ItemState) => (
        <Input
          value={v}
          size="small"
          onChange={(e) =>
            handleUpdateActionTaken(record.item.fm110_01, e.target.value)
          }
          placeholder="（選填）"
          style={{ minWidth: 160 }}
        />
      ),
    },
    {
      title: '異常',
      dataIndex: ['item', 'fm110_21'],
      key: 'fm110_21',
      width: 60,
      align: 'center',
      render: (v: boolean) => (
        <Checkbox checked={v} disabled />
      ),
    },
  ];

  // -- Render --
  return (
    <div className="m501-form">
      {(loading || approveLoading) && (
        <div className="loading-overlay">
          {approveLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                庫存管理
              </a>
              <span>/</span>
              <span className="current">正常產品異動調整核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>正常產品異動調整核准</h1>
            <span className="badge">M501</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fm110_01">調整單 從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onBlur={onStartBlur}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 130 }}
            maxLength={5}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 130 }}
            allowClear
          />
          <Button type="primary" data-acceptance-action="PWM501-ACT-QUERY" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fm110_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無待核准調整單' }}
            />
          ) : (
            <div className="empty-state">
              請輸入調整單號範圍並點擊「確定範圍」查詢待核准調整單
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            data-acceptance-action="PWM501-ACT-APPROVE"
            type="primary"
            size="large"
            disabled={selectedCount === 0 || approveLoading}
            loading={approveLoading}
            onClick={onApprove}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwm501f1AcceptanceManifest}
          records={candidateRecords}
          selectedKeys={selectedKeys}
          keyOf={(item) => item.fm110_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={lastApiResponse}
          loading={loading || approveLoading}
          onAutoFill={() => { if (candidateRecords[0]) handleToggleItem(candidateRecords[0].fm110_01, true); }}
          autoFillDisabled={candidateRecords.length === 0}
          autoFillReason="請先查詢待核准調整單；自動填表只會勾選第一筆範例，不會執行核准。"
          screenDraft={{ startNo, endNo, selected_items: selectedKeys, candidates: candidateRecords }}
        />
      </div>
    </div>
  );
};

export default M501Form;
